<template>
  <article
    ref="photoGallery"
    class="page-event"
    :style="!loadingEvent ? `background-image: url(${event.eventBackgroundImage || defaultBackground})` : ''"
  >
    <AppSpinner v-if="loadingEvent" />

    <section v-else>
      <template v-if="!event.photoCount">
        <EventBannerNoPhotos :event="event" />
      </template>

      <template v-else>
        <EventBanner
          :event="event"
          :class="{ 'pb-10 xs:pb-16': !showBibNumberInput }"
        >
          <BibNumberSearch
            v-if="showBibNumberInput"
            v-model="searchParams"
            @search="handleSearch"
          />
        </EventBanner>

        <!-- Search -->
        <section class="section-event">
          <EventTextSearch
            v-if="!showBibNumberInput"
            v-model:search-params="searchParams"
            v-model:protected-category="isCategoryProtected"
            :metadata="event.searchableMetadata"
            @handle-search="handleSearch"
          />

          <EventCategorySearch
            v-if="event.categories"
            v-model:search-params="searchParams"
            v-model:protected-category="isCategoryProtected"
            :class="{'pt-0': !showBibNumberInput}"
            :categories="event.categories"
            :loading="loadingEvent"
            @handle-search="handleSearch"
          />
        </section>

        <template v-if="isCategoryProtected && !isUserVerified">
          <ProtectedCategoryPlaceholder />
        </template>

        <template v-else>
          <section
            id="photoGallery"
            class="section-event"
          >
            <template v-if="photos.length <= 0 && !loadingEventPhotos">
              <h2 class="text-center mb-0 py-16">
                Sorry,<br>
                {{ noPhotosMessage }}
              </h2>
            </template>

            <template v-else>
              <PhotoGallery
                :event="event"
                :photos="photos"
                :photo-pack-count="photoPackCount"
                :searched-bib-number="searchedBibNumber"
                :loading="loadingEventPhotos"
                :selected-category="selectedCategory"
                @search="handleSearch"
              />
            </template>
          </section>

          <template v-if="loadingEventPhotos">
            <PhotoGalleryLoader class="min-h-screen" />
          </template>
        </template>
      </template>
    </section>
  </article>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, onMounted, provide, ref, useTemplateRef, watch } from 'vue';
import { useInfiniteScroll } from '@vueuse/core';
import { useHead } from '@vueuse/head';
import { useRoute, useRouter } from 'vue-router';

import { AppSpinner } from '@/components/app';
import { EventBanner, EventBannerNoPhotos } from '@/components/banners';
import { BibNumberSearch, EventCategorySearch, EventTextSearch } from '@/components/forms/search';
import { PhotoGallery } from '@/components/galleries';
import { PhotoGalleryLoader } from '@/components/loaders';
import { ProtectedCategoryPlaceholder } from '@/components/placeholders';

import { defaultBackground } from '@/assets/images/views/event-show';
import { useHttp } from '@/composables';
import { useAuthStore } from '@/store';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const loadingEvent = ref();
const loadingEventPhotos = ref();
const event = ref({});

const photos = ref([]);
const searchParams = ref({
  searchBy: 'bibNumber',
});

provide('searchParams', searchParams.value);

// const showGalleryLoader = ref(true);
const searchedBibNumber = ref(null);
const showBibNumberInput = ref(false);
const selectedCategory = ref(null);
const photoGallery = useTemplateRef('photoGallery');
const photoPackCount = ref(null);

// This indicates whether the category itself is protected or not
const isCategoryProtected = ref(false);
const isUserVerified = ref(authStore.user?.isVerified ?? false);
// const isScrollLoading = ref(false);
const pageTitle = computed(() => event.value ? event.value.name : '');
const ogImageUrl = computed(() => event.value ? event.value.eventImage : '');

const noPhotosMessage = computed(() => {
  return searchParams.value.search ? 'we couldn\'t find any photos related to your search.' : 'there are currently no photos in this category.';
});

onMounted(async () => {
  await loadEvent();

  if (event.value) {
    showBibNumberInput.value = !event.value.searchableMetadata.length || (event.value.searchableMetadata.length === 1 && event.value.searchableMetadata.includes('bibNumber'));

    await loadPhotos();
  }
});

const loadEvent = async () => {
  loadingEvent.value = true;
  const response = await useHttp().get(`/events/${route.params.slug}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    event.value = response.data;
  } else if (response.statusCode === HttpStatusCode.NotFound) {
    await router.push({ name: 'app_not_found' });
  }

  loadingEvent.value = false;
};

// Function to load photos
const loadPhotos = async () => {
  loadingEventPhotos.value = true;

  if (event.value.photoCount === 0) {
    loadingEventPhotos.value = false;

    return;
  }

  const response = await useHttp().get(`/events/${route.params.slug}/photos`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    searchParams.value.cursor = response.data.pagination.nextCursor;
    selectedCategory.value = searchParams.value.category;

    photos.value.push(...response.data.items);
    photoPackCount.value = response.data.photoPackCount;

    photos.value.forEach(image => {
      const img = new Image();

      img.src = image.thumbnail.url;
      img.onload = () => {
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        image.aspectClass = aspectRatio > 1 ? 'landscape' : 'portrait';
      };
    });
  }

  loadingEventPhotos.value = false;
};

const handleSearch = (gallerySearchParams) => {
  if (gallerySearchParams?.search) {
    searchParams.value = JSON.parse(JSON.stringify(gallerySearchParams));
  }

  searchParams.value.cursor = null;
  photos.value = [];

  loadPhotos();

  if (searchParams.value.searchBy === 'bibNumber') {
    searchedBibNumber.value = parseInt(searchParams.value.search);
  } else {
    searchedBibNumber.value = null;
  }
};

useHead({
  title: pageTitle,
  meta: [
    {
      property: 'og:title',
      content: pageTitle,
    },
    {
      property: 'og:image',
      content: ogImageUrl,
    },
    {
      name: 'twitter:title',
      content: pageTitle,
    },
    {
      name: 'twitter:image',
      content: ogImageUrl,
    },
  ],
});

useInfiniteScroll(
  window,
  async () => {
    await loadPhotos();
  },
  {
    distance: 200,
    canLoadMore: () => !loadingEvent.value && event.value && searchParams.value.cursor !== null,
  },
);

// When a verified user logs in the protected categories need to be refreshed
watch(() => authStore.user?.isVerified, (newValue) => {
  // Handle undefined on logout
  const verified = newValue ?? false;
  // The `isUserVerified` needs to be updated to reflect the verification status of the current user
  isUserVerified.value = verified;

  // Handle search needs to be called again if the user is on a protected category
  if (isCategoryProtected.value) {
    handleSearch(searchParams.value);
  }
}, { immediate: true });

</script>
